import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { customerAPI, RegisterRequest, LoginRequest, CustomerUpload, CustomerCase, resetRefreshFailureCounter } from '../../services/customerAPI';
import axios, { AxiosError } from 'axios';
import { setTokenExpiry, clearTokenExpiry, broadcastAuthEvent, AUTH_EVENTS } from '../../hooks/useTokenExpiration';

// Customer user interface
interface CustomerUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  emailVerified: boolean;
  status: string;
}

// Registration state
interface RegistrationState {
  loading: boolean;
  success: boolean;
  error: string | null;
  validationErrors: Record<string, string>;
}

// Dashboard data state
interface DashboardUploadsState {
  data: CustomerUpload[];
  loading: boolean;
  error: string | null;
  total: number;
}

interface DashboardCasesState {
  data: CustomerCase[];
  loading: boolean;
  error: string | null;
  total: number;
}

interface DashboardState {
  uploads: DashboardUploadsState;
  cases: DashboardCasesState;
}

// Customer auth state
interface CustomerAuthState {
  user: CustomerUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  registration: RegistrationState;
  dashboard: DashboardState;
}

const initialState: CustomerAuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  registration: {
    loading: false,
    success: false,
    error: null,
    validationErrors: {},
  },
  dashboard: {
    uploads: {
      data: [],
      loading: false,
      error: null,
      total: 0,
    },
    cases: {
      data: [],
      loading: false,
      error: null,
      total: 0,
    },
  },
};

// Helper to check if error is a cancelled request (Task #4212)
const isRequestCancelled = (error: unknown): boolean => {
  return axios.isCancel(error);
};

// Helper to extract error message from axios error
const getErrorMessage = (error: unknown): string => {
  // Handle cancelled requests gracefully (Task #4212)
  if (isRequestCancelled(error)) {
    return 'Request cancelled';
  }
  if (error instanceof AxiosError) {
    const response = error.response?.data;
    if (response?.message) {
      return response.message;
    }
    if (response?.error) {
      return response.error;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

// Helper to extract validation errors from axios error
const getValidationErrors = (error: unknown): Record<string, string> => {
  if (error instanceof AxiosError) {
    const response = error.response?.data;
    if (response?.errors && typeof response.errors === 'object') {
      return response.errors;
    }
  }
  return {};
};

// Task #4214: Helper to validate API response structure
// Validates that response.data.data exists and contains required fields
// Returns the validated data or null if validation fails
const validateApiResponse = <T>(
  response: { data?: { data?: unknown } } | undefined,
  requiredFields: string[]
): T | null => {
  const result = response?.data?.data;

  // Check that result exists and is an object
  if (!result || typeof result !== 'object' || Array.isArray(result)) {
    return null;
  }

  // Check that all required fields exist
  const resultObj = result as Record<string, unknown>;
  for (const field of requiredFields) {
    if (!(field in resultObj) || resultObj[field] === undefined) {
      return null;
    }
  }

  return result as T;
};

// Async thunks
// All thunks support abort signals (Task #4212) for cancellation on unmount
export const registerCustomer = createAsyncThunk<
  { id: string; email: string; firstName: string; lastName: string },
  RegisterRequest,
  { rejectValue: { message: string; validationErrors: Record<string, string> } }
>(
  'customerAuth/register',
  async (data, { rejectWithValue, signal }) => {
    try {
      const response = await customerAPI.register(data, { signal });
      // Task #4214: Validate response structure before accessing fields
      interface RegisterResponse {
        id: string;
        email: string;
        first_name: string;
        last_name: string;
      }
      const result = validateApiResponse<RegisterResponse>(
        response,
        ['id', 'email', 'first_name', 'last_name']
      );
      if (!result) {
        return rejectWithValue({
          message: 'Invalid server response',
          validationErrors: {},
        });
      }
      return {
        id: result.id,
        email: result.email,
        firstName: result.first_name,
        lastName: result.last_name,
      };
    } catch (error) {
      // Don't show error UI for cancelled requests
      if (isRequestCancelled(error)) {
        return rejectWithValue({
          message: 'Request cancelled',
          validationErrors: {},
        });
      }
      return rejectWithValue({
        message: getErrorMessage(error),
        validationErrors: getValidationErrors(error),
      });
    }
  }
);

export const loginCustomer = createAsyncThunk<
  { user: CustomerUser; expiresIn: number },
  LoginRequest,
  { rejectValue: { message: string; validationErrors: Record<string, string> } }
>(
  'customerAuth/login',
  async (data, { rejectWithValue, signal }) => {
    try {
      const response = await customerAPI.login(data, { signal });
      // Task #4214: Validate response structure before accessing fields
      interface LoginResponse {
        id: string;
        email: string;
        first_name: string;
        last_name: string;
        display_name?: string;
        expires_in: number;
      }
      const result = validateApiResponse<LoginResponse>(
        response,
        ['id', 'email', 'first_name', 'last_name', 'expires_in']
      );
      if (!result) {
        return rejectWithValue({
          message: 'Invalid server response',
          validationErrors: {},
        });
      }
      // Note: Backend returns user data at root level, not nested under 'user'
      // Tokens are NOT in response - they are set as httpOnly cookies by server
      return {
        user: {
          id: result.id,
          email: result.email,
          firstName: result.first_name,
          lastName: result.last_name,
          displayName: result.display_name,
          emailVerified: true, // Email must be verified to login successfully
          status: 'active', // User must be active to login successfully
        },
        expiresIn: result.expires_in,
      };
    } catch (error) {
      if (isRequestCancelled(error)) {
        return rejectWithValue({
          message: 'Request cancelled',
          validationErrors: {},
        });
      }
      return rejectWithValue({
        message: getErrorMessage(error),
        validationErrors: getValidationErrors(error),
      });
    }
  }
);

export const fetchCustomerProfile = createAsyncThunk<
  CustomerUser,
  void,
  { rejectValue: string }
>(
  'customerAuth/fetchProfile',
  async (_, { rejectWithValue, signal }) => {
    try {
      const response = await customerAPI.getProfile({ signal });
      // Task #4214: Validate response structure before accessing fields
      interface ProfileResponse {
        id: string;
        email: string;
        first_name: string;
        last_name: string;
        display_name?: string;
        email_verified: boolean;
        status: string;
      }
      const result = validateApiResponse<ProfileResponse>(
        response,
        ['id', 'email', 'first_name', 'last_name', 'email_verified', 'status']
      );
      if (!result) {
        return rejectWithValue('Invalid server response');
      }
      return {
        id: result.id,
        email: result.email,
        firstName: result.first_name,
        lastName: result.last_name,
        displayName: result.display_name,
        emailVerified: result.email_verified,
        status: result.status,
      };
    } catch (error) {
      if (isRequestCancelled(error)) {
        return rejectWithValue('Request cancelled');
      }
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const refreshCustomerToken = createAsyncThunk<
  { expiresIn: number },
  void,
  { rejectValue: string }
>(
  'customerAuth/refreshToken',
  async (_, { rejectWithValue, signal }) => {
    try {
      // Refresh token is sent via httpOnly cookie
      // New tokens are set as httpOnly cookies by server response
      const response = await customerAPI.refreshToken({ signal });
      // Task #4214: Validate response structure before accessing fields
      interface RefreshResponse {
        expires_in: number;
      }
      const result = validateApiResponse<RefreshResponse>(
        response,
        ['expires_in']
      );
      if (!result) {
        return rejectWithValue('Invalid server response');
      }
      return {
        expiresIn: result.expires_in,
      };
    } catch (error) {
      if (isRequestCancelled(error)) {
        return rejectWithValue('Request cancelled');
      }
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Logout async thunk - calls server to clear httpOnly cookies
export const logoutCustomerAsync = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>(
  'customerAuth/logoutAsync',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      // Call server to clear httpOnly cookies
      await customerAPI.logout();
      // Clear client-side state (this also broadcasts to other tabs)
      dispatch(logoutCustomer());
    } catch (error) {
      // Even if server call fails, still clear client-side state
      dispatch(logoutCustomer());
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Dashboard async thunks
export const fetchCustomerUploads = createAsyncThunk<
  { uploads: CustomerUpload[]; total: number },
  { page?: number; page_size?: number } | void,
  { rejectValue: string }
>(
  'customerAuth/fetchUploads',
  async (params, { rejectWithValue, signal }) => {
    try {
      const response = await customerAPI.getMyUploads(params || { page: 1, page_size: 5 }, { signal });
      // Task #4214: Validate response structure before accessing fields
      const result = response?.data?.data;
      if (!result || typeof result !== 'object') {
        return rejectWithValue('Invalid server response');
      }
      return {
        uploads: Array.isArray(result.files) ? result.files : [],
        total: typeof result.total === 'number' ? result.total : 0,
      };
    } catch (error) {
      if (isRequestCancelled(error)) {
        return rejectWithValue('Request cancelled');
      }
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchCustomerCases = createAsyncThunk<
  { cases: CustomerCase[]; total: number },
  { page?: number; page_size?: number } | void,
  { rejectValue: string }
>(
  'customerAuth/fetchCases',
  async (params, { rejectWithValue, signal }) => {
    try {
      const response = await customerAPI.getMyCases(params || { page: 1, page_size: 5 }, { signal });
      // Task #4214: Validate response structure before accessing fields
      const result = response?.data?.data;
      if (!result || typeof result !== 'object') {
        return rejectWithValue('Invalid server response');
      }
      return {
        cases: Array.isArray(result.cases) ? result.cases : [],
        total: typeof result.total === 'number' ? result.total : 0,
      };
    } catch (error) {
      if (isRequestCancelled(error)) {
        return rejectWithValue('Request cancelled');
      }
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Slice
export const customerAuthSlice = createSlice({
  name: 'customerAuth',
  initialState,
  reducers: {
    // Clear registration state (to start fresh)
    clearRegistration: (state) => {
      state.registration = {
        loading: false,
        success: false,
        error: null,
        validationErrors: {},
      };
    },
    // Logout customer (clears ALL Redux state including dashboard data)
    // SECURITY: Must clear ALL state to prevent data leakage on shared computers
    // Note: httpOnly cookies are cleared by calling customerAPI.logout()
    // This reducer clears Redux state - the async thunk handles the API call
    logoutCustomer: () => {
      // Clear token expiry from localStorage BEFORE returning initialState
      clearTokenExpiry();
      // Broadcast logout to other tabs
      broadcastAuthEvent(AUTH_EVENTS.LOGOUT);
      // SECURITY: Return full initialState to clear ALL data including dashboard
      // This prevents customer uploads/cases from being visible to next user
      return initialState;
    },
    // Set customer user directly (e.g., from profile fetch after auth check)
    setCustomerUser: (state, action: PayloadAction<CustomerUser>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Register
    builder
      .addCase(registerCustomer.pending, (state) => {
        state.registration.loading = true;
        state.registration.error = null;
        state.registration.validationErrors = {};
        state.registration.success = false;
      })
      .addCase(registerCustomer.fulfilled, (state) => {
        state.registration.loading = false;
        state.registration.success = true;
        state.registration.error = null;
        state.registration.validationErrors = {};
      })
      .addCase(registerCustomer.rejected, (state, action) => {
        state.registration.loading = false;
        state.registration.success = false;
        state.registration.error = action.payload?.message || 'Registration failed';
        state.registration.validationErrors = action.payload?.validationErrors || {};
      });

    // Login
    builder
      .addCase(loginCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.error = null;
        // Store token expiry time in localStorage for client-side expiration monitoring
        // Note: Actual tokens are stored in httpOnly cookies by server response
        setTokenExpiry(action.payload.expiresIn);
        // Task #4376: Reset refresh failure counter on successful login
        // This ensures user starts fresh after re-authentication
        resetRefreshFailureCounter();
      })
      .addCase(loginCustomer.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload?.message || 'Login failed';
      });

    // Fetch profile
    builder
      .addCase(fetchCustomerProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(fetchCustomerProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch profile';
      });

    // Refresh token
    builder
      .addCase(refreshCustomerToken.fulfilled, (_state, action) => {
        // Update token expiry time in localStorage
        // Note: New tokens are set as httpOnly cookies by server response
        setTokenExpiry(action.payload.expiresIn);
        // Broadcast to other tabs that token was refreshed
        broadcastAuthEvent(AUTH_EVENTS.TOKEN_REFRESHED, { expiresIn: action.payload.expiresIn });
      })
      .addCase(refreshCustomerToken.rejected, () => {
        // Token refresh failed - clear ALL state for security
        // SECURITY: Must clear ALL state to prevent data leakage on shared computers
        // Clear token expiry
        clearTokenExpiry();
        // Broadcast logout to other tabs
        broadcastAuthEvent(AUTH_EVENTS.LOGOUT);
        // Return full initialState to clear ALL data including dashboard
        return initialState;
      });

    // Fetch uploads (dashboard)
    // SECURITY: Auth guards prevent race condition where logout occurs
    // during in-flight request, and stale data overwrites cleared state
    builder
      .addCase(fetchCustomerUploads.pending, (state) => {
        // Only set loading if authenticated (prevents race condition)
        if (state.isAuthenticated) {
          state.dashboard.uploads.loading = true;
          state.dashboard.uploads.error = null;
        }
      })
      .addCase(fetchCustomerUploads.fulfilled, (state, action) => {
        // SECURITY: Only populate data if still authenticated
        // This prevents stale data from appearing after logout
        if (state.isAuthenticated) {
          state.dashboard.uploads.loading = false;
          state.dashboard.uploads.data = action.payload.uploads;
          state.dashboard.uploads.total = action.payload.total;
          state.dashboard.uploads.error = null;
        }
      })
      .addCase(fetchCustomerUploads.rejected, (state, action) => {
        if (state.isAuthenticated) {
          state.dashboard.uploads.loading = false;
          state.dashboard.uploads.error = action.payload || 'Failed to fetch uploads';
        }
      });

    // Fetch cases (dashboard)
    // SECURITY: Same auth guards as uploads
    builder
      .addCase(fetchCustomerCases.pending, (state) => {
        if (state.isAuthenticated) {
          state.dashboard.cases.loading = true;
          state.dashboard.cases.error = null;
        }
      })
      .addCase(fetchCustomerCases.fulfilled, (state, action) => {
        // SECURITY: Only populate data if still authenticated
        if (state.isAuthenticated) {
          state.dashboard.cases.loading = false;
          state.dashboard.cases.data = action.payload.cases;
          state.dashboard.cases.total = action.payload.total;
          state.dashboard.cases.error = null;
        }
      })
      .addCase(fetchCustomerCases.rejected, (state, action) => {
        if (state.isAuthenticated) {
          state.dashboard.cases.loading = false;
          state.dashboard.cases.error = action.payload || 'Failed to fetch cases';
        }
      });
  },
});

export const { clearRegistration, logoutCustomer, setCustomerUser, clearError } = customerAuthSlice.actions;

// Task #4214: Export validateApiResponse for testing
export { validateApiResponse };
export default customerAuthSlice.reducer;
